import { Link } from "react-router-dom";
import { Linkedin, Instagram, ArrowUpRight } from "lucide-react";
import { openCookieSettings } from "@/components/CookieConsent";
import Logo from "@/components/Logo";

const Col = ({ index, title, children }: { index: string; title: string; children: React.ReactNode }) => (
  <div className="space-y-5">
    <div className="flex items-baseline gap-2.5">
      <span className="font-sans text-[10px] tabular-nums text-foreground/40 tracking-[0.18em]">
        {index}
      </span>
      <h4 className="font-sans text-[10px] font-medium uppercase tracking-[0.22em] text-foreground/55">
        {title}
      </h4>
    </div>
    <div className="flex flex-col gap-3 font-sans text-[14px] text-foreground/70">
      {children}
    </div>
  </div>
);

const FLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <Link
    to={to}
    className="group/link inline-flex items-center gap-1.5 hover:text-foreground transition-colors w-fit"
  >
    <span className="border-b border-transparent group-hover/link:border-foreground/30 transition-colors">
      {children}
    </span>
  </Link>
);

export const EditorialFooter = () => {
  const year = new Date().getFullYear();

  return (
    <footer className="bg-background relative overflow-hidden border-t border-border/50">
      {/* Soft sun glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80vw] h-[40vw] bg-gradient-to-b from-accent/40 to-transparent blur-3xl rounded-full pointer-events-none -z-0" />

      <div className="relative max-w-6xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-10">
        {/* Top eyebrow row */}
        <div className="flex items-center justify-between gap-4 flex-wrap mb-12 md:mb-14">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/15 bg-primary/5 backdrop-blur px-3.5 py-1.5">
            <span className="size-1.5 rounded-full bg-primary animate-pulse" />
            <span className="font-sans text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-medium text-primary/85">
              Always Anonymous
            </span>
          </div>
          <span className="font-sans text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-medium text-foreground/45 tabular-nums">
            The Closing Note
          </span>
        </div>

        {/* Link grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-8 mb-14 pb-12 border-t border-border/60 pt-12">
          <Col index="01" title="Product">
            <FLink to="/#features">Features</FLink>
            <FLink to="/#how-it-works">How it works</FLink>
            <FLink to="/pricing">Pricing</FLink>
            <FLink to="/#faq">FAQ</FLink>
          </Col>
          <Col index="02" title="Solutions">
            <FLink to="/solutions/hr-teams">HR Teams</FLink>
            <FLink to="/solutions/remote-teams">Remote Teams</FLink>
            <FLink to="/solutions/enterprise">Enterprise</FLink>
            <FLink to="/use-cases/exit-interviews">Exit Interviews</FLink>
          </Col>
          <Col index="03" title="Company">
            <FLink to="/about">About</FLink>
            <FLink to="/contact">Contact</FLink>
            <FLink to="/how-anonymity-works">Anonymity</FLink>
            <FLink to="/help">Help</FLink>
          </Col>
          <Col index="04" title="Legal">
            <FLink to="/privacy-policy">Privacy</FLink>
            <FLink to="/terms-of-service">Terms</FLink>
            <FLink to="/cookie-policy">Cookies</FLink>
            <button
              onClick={openCookieSettings}
              className="group/link inline-flex items-center gap-1.5 hover:text-foreground transition-colors text-left w-fit font-sans text-[14px] text-foreground/70"
            >
              <span className="border-b border-transparent group-hover/link:border-foreground/30 transition-colors">
                Settings
              </span>
            </button>
          </Col>
        </div>

        {/* Bottom */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-5 pt-2">
          <div className="flex items-center gap-4">
            <Logo size="md" to="/" />
            <span className="font-sans text-[12px] text-foreground/45 tabular-nums">
              © {year} · All rights reserved
            </span>
          </div>

          <div className="flex items-center gap-2">
            <a
              href="https://www.linkedin.com/company/sendfeedback/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="h-9 w-9 flex items-center justify-center rounded-full border border-border/70 bg-card/60 text-foreground/65 hover:bg-primary hover:text-primary-foreground hover:border-primary hover:-translate-y-0.5 transition-all duration-300"
            >
              <Linkedin className="h-3.5 w-3.5" strokeWidth={1.75} />
            </a>
            <a
              href="https://www.instagram.com/sendfeedback.ai"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="h-9 w-9 flex items-center justify-center rounded-full border border-border/70 bg-card/60 text-foreground/65 hover:bg-primary hover:text-primary-foreground hover:border-primary hover:-translate-y-0.5 transition-all duration-300"
            >
              <Instagram className="h-3.5 w-3.5" strokeWidth={1.75} />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
